import React, { useContext, useMemo } from 'react';
import moment from 'moment';
import styled, { css } from 'styled-components';
import { FieldTypes } from '@ubeya/shared/hooks/account/useFields';
import useFormattingMap from '@ubeya/shared-web/hooks/useFormattingMap';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { Tooltip } from '@ubeya/shared-web/components';
import GeneralAttachmentRenderer from './GeneralAttachmentRenderer';

const DAYS_UNTIL_ATTACHMENT_EXPIRE = 30;
const DAYS_UNTIL_FIELD_EXPIRE = 14;

const Value = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 0 8px;
  ${({ theme, $isExpired, $isAboutToExpire }) =>
    $isExpired
      ? css`
          background-color: ${() => `${theme.colors.redNegative}1A`};
          color: ${() => theme.colors.redNegative};
        `
      : $isAboutToExpire &&
        css`
          background-color: ${() => `${theme.colors.orangeChanges}1A`};
          color: ${() => theme.colors.yellowPartially};
        `}
`;

const isFieldAboutToExpire = (date, isAttachment) => {
  const today = moment().startOf('day');
  const expirationDate = moment(date).startOf('day');
  const differenceInDays = expirationDate.diff(today, 'days');
  const limit = isAttachment ? DAYS_UNTIL_ATTACHMENT_EXPIRE : DAYS_UNTIL_FIELD_EXPIRE;
  return {
    isAboutToExpire: differenceInDays <= limit && differenceInDays > 0,
    daysUntilExpired: differenceInDays
  };
};

const FieldValueRenderer = ({ employeeId, firstName, lastName, fieldData, setSelectedField, value }) => {
  const { t } = useContext(ConfigContext);
  const { date: dateFormatter } = useFormattingMap();

  const { value: fieldValue, fieldTypeId } = fieldData || {};
  const { boolean: booleanValue, options, date, expirationDate } = fieldValue || {};

  const { isExpired, isAboutToExpire, daysUntilExpired } = useMemo(() => {
    const fieldExpirationDate = expirationDate ?? date ?? value;
    if (!fieldExpirationDate || !moment(fieldExpirationDate).isValid()) {
      return {
        isExpired: false,
        isAboutToExpire: false
      };
    }
    const isFieldAboutToExpireData = isFieldAboutToExpire(
      fieldExpirationDate,
      fieldTypeId === FieldTypes.FIELD_TYPE_DOCUMENT
    );

    return {
      isExpired: moment().startOf('day').isSameOrAfter(moment(fieldExpirationDate).startOf('day')),
      ...isFieldAboutToExpireData
    };
  }, [date, expirationDate, fieldTypeId, value]);

  if (!fieldData) {
    return null;
  }

  switch (fieldTypeId) {
    case FieldTypes.FIELD_TYPE_DOCUMENT:
      return (
        <Tooltip body={t('complianceAboutToExpireTooltip', { count: daysUntilExpired })} disabled={!isAboutToExpire}>
          <GeneralAttachmentRenderer
            employee={{ id: employeeId, firstName, lastName }}
            setSelectedField={setSelectedField}
            fieldData={fieldData}
            isExpired={isExpired}
            isAboutToExpire={isAboutToExpire}
          />
        </Tooltip>
      );
    case FieldTypes.FIELD_TYPE_BOOLEAN:
      return <Value>{value || booleanValue ? t('yes') : ''}</Value>;
    case FieldTypes.FIELD_TYPE_DATE:
      return (
        <Tooltip body={t('complianceAboutToExpireTooltip', { count: daysUntilExpired })} disabled={!isAboutToExpire}>
          <Value $isExpired={isExpired} $isAboutToExpire={isAboutToExpire}>
            {value || date ? dateFormatter(value || date) : ''}
          </Value>
        </Tooltip>
      );
    case FieldTypes.FIELD_TYPE_OPTIONS:
      return <Value>{(value || options)?.map((option) => t(option.value)).join(', ')}</Value>;
    default:
      return null;
  }
};

export default FieldValueRenderer;
